import { AgGridAngular } from '@ag-grid-community/angular';
import {
  ColDef,
  ColGroupDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
} from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { distinctUntilArrayItemChanged } from '@datorama/akita';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';

import {
  AgBudgetAttributeComponentParams,
  AgBudgetGroupHeaderComponent,
} from '@features/ag-budget-group-header/ag-budget-group-header.component';
import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { AgTooltipComponent } from '@features/inline-budget/ag-tooltip.component';
import { SaveChangesComponent } from '@features/save-changes/save-changes.component';

import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import {
  EntityType,
  EventType,
  GqlService,
  listPermissionsQuery,
  PermissionType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { TrialUserQuery } from '@shared/store/trial-users/trial-user.query';
import { TrialUserService } from '@shared/store/trial-users/trial-user.service';
import { ExportType, Utils } from '@shared/utils/utils';

import { AgGroupCheckboxComponent, ICheckboxCellParams } from './ag-group-checkbox.component';

dayjs.extend(utc);
dayjs.extend(timezone);

type UserPermissionsGridData = listPermissionsQuery & {
  [k: string]: boolean;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    AgGridAngular,
    FormsModule,
    TableSkeletonComponent,
    SaveChangesComponent,
    ExportExcelButtonComponent,
    StickyGridDirective,
  ],
  selector: 'aux-user-permissions',
  styleUrl: './user-permissions.component.scss',
  templateUrl: './user-permissions.component.html',
})
export class UserPermissionsComponent implements OnInit {
  private authQuery = inject(AuthQuery);
  private authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private eventService = inject(EventService);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private trialUserQuery = inject(TrialUserQuery);
  private trialUserService = inject(TrialUserService);

  gridAPI = signal<GridApi | undefined>(undefined);
  loading = signal(true);

  autoGroupColumnDef: ColDef = {
    cellClass: [TableConstants.STYLE_CLASSES.CELL_ALIGN_LEFT, 'custom-group-icon'],
    cellRendererParams: {
      suppressCount: true,
    },
    field: 'permission',
    headerClass: 'permission-header',
    headerComponent: AgBudgetGroupHeaderComponent,
    headerComponentParams: {
      alignHeaderOneRowHeight: true,
      columnsToCollapse: [] as string[],
      expandLevel: () => 2,
      localStorageKey: LocalStorageKey.USER_PERMISSION_STORAGE,
      quickFilterText: true,
      template: `Permission`,
      templateClasses: ['text-[16px] font-semibold'],
    } as AgBudgetAttributeComponentParams,
    headerName: 'Permission',
    minWidth: 400,
    pinned: 'left',
    resizable: true,
    width: 400,
  };
  changedValues: {
    permission: string;
    sub: string;
    value: boolean;
  }[] = [];
  defaultColumns: ColDef[] = [
    {
      field: 'permission',
      hide: true,
    },
    {
      field: 'permission_group',
      hide: true,
      rowGroup: true,
    },
    {
      field: 'permission_sub_group',
      hide: true,
      rowGroup: true,
    },
  ];
  gridData$ = new BehaviorSubject<UserPermissionsGridData[]>([]);
  gridOptions: GridOptions<UserPermissionsGridData> = {
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    autoGroupColumnDef: this.autoGroupColumnDef,
    defaultColDef: {
      resizable: true,
      sortable: false,
      suppressMenu: true,
      suppressMovable: true,
      tooltipComponent: AgTooltipComponent,
    },
    getRowHeight: (params) => {
      if (params.node.group && params.node.level === 0) {
        return 50;
      }

      return 35;
    },
    groupAllowUnbalanced: true,
    groupDefaultExpanded: -1,
    initialGroupOrderComparator: ({ nodeA, nodeB }) => {
      if (nodeA.key === 'Checklist') {
        return -1;
      }

      if (nodeB.key === 'Checklist') {
        return 1;
      }

      if (nodeA.key === 'Invoices') {
        return -1;
      }

      if (nodeB.key === 'Invoices') {
        return 1;
      }

      return 0;
    },
    rowClassRules: {
      'custom-group-background': (params) => !!params.node.group && params.node.level === 0,
    },
    suppressCellFocus: true,
    suppressColumnVirtualisation: true,
    suppressMenuHide: true,
    suppressPropertyNamesCheck: true,
    suppressRowClickSelection: true,
    tooltipShowDelay: 0,
  };
  gridOptions$ = new BehaviorSubject<GridOptions<UserPermissionsGridData>>(this.gridOptions);
  isSysAdmin$ = this.authService.isAuthorized$({
    sysAdminsOnly: true,
  });
  loggedInUserHasAdministration$ = this.authService.isAuthorized$({
    permissions: [PermissionType.PERMISSION_UPDATE_USER_PERMISSIONS],
    sysAdminsOnly: false,
  });
  matrixUsers$ = this.trialUserQuery
    .selectAll()
    .pipe(
      map((users) => {
        return users
          .filter((user) => {
            // todo: remove this when we get superAdmin flag
            return user.email ? !user.email.includes('@auxili.us') : true;
          })
          .sort((x, y) => {
            return Utils.alphaNumSort(
              `${x.given_name} ${x.family_name}` as string,
              `${y.given_name} ${y.family_name}` as string
            );
          });
      })
    )
    .pipe(distinctUntilArrayItemChanged());
  permissions$ = new BehaviorSubject<listPermissionsQuery[]>([]);
  reset$ = new BehaviorSubject(null);

  ngOnInit(): void {
    this.mainQuery
      .select('trialKey')
      .pipe(
        switchMap(() => this.gqlService.listPermissions$()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((x) => {
        const period_close_permissions: listPermissionsQuery[] = [];
        const budget_permissions: listPermissionsQuery[] = [];
        const forecast_permissions: listPermissionsQuery[] = [];
        const investigator_permissions: listPermissionsQuery[] = [];
        const administration_permissions: listPermissionsQuery[] = [];
        const other_permissions: listPermissionsQuery[] = [];

        x.data?.forEach((x) => {
          if (x.permission_group === 'Period Close') {
            period_close_permissions.push(x);
          } else if (x.permission_group === 'Budgets') {
            budget_permissions.push(x);
          } else if (x.permission_group === 'Forecast Settings') {
            forecast_permissions.push(x);
          } else if (x.permission_group === 'Investigator') {
            investigator_permissions.push(x);
          } else if (x.permission_group === 'Administration') {
            administration_permissions.push(x);
          } else {
            other_permissions.push(x);
          }
        });

        this.permissions$.next([
          ...period_close_permissions,
          ...budget_permissions,
          ...forecast_permissions,
          ...investigator_permissions,
          ...administration_permissions,
          ...other_permissions,
        ]);
      });

    combineLatest([this.permissions$, this.matrixUsers$, this.reset$])
      .pipe(
        map(([permissions, users]) => {
          this.gridData$.next(
            users.length
              ? permissions.map((permission) => {
                  const userPerms = users.reduce(
                    (acc, user) => {
                      acc[user.sub] = user.permissions[permission.id] === 'E';

                      return acc;
                    },
                    {} as Record<string, boolean>
                  );

                  return <UserPermissionsGridData>{
                    ...permission,
                    permission_group: permission.permission_group || '',
                    permission_sub_group: permission.permission_sub_group || '',
                    ...userPerms,
                  };
                })
              : []
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  async canDeactivate(): Promise<boolean> {
    if (this.changedValues.length) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  // eslint-disable-next-line max-lines-per-function
  constructor() {
    combineLatest([this.gridData$, this.isSysAdmin$, this.loggedInUserHasAdministration$])
      .pipe(debounceTime(0), takeUntilDestroyed())
      // eslint-disable-next-line @typescript-eslint/no-misused-promises, max-lines-per-function
      .subscribe(async ([data, isSysAdmin, loggedInUserHasAdministration]) => {
        const users = await firstValueFrom(this.matrixUsers$);

        this.loading.set(true);

        const userSub = this.authQuery.getValue().sub;
        const columnDefs: (
          | ColDef<UserPermissionsGridData>
          | ColGroupDef<UserPermissionsGridData>
        )[] = [...this.defaultColumns];

        this.changedValues = [];

        if (users.length && data.length) {
          columnDefs.push({
            children: users.map((user) => {
              return {
                cellClass: [
                  TableConstants.STYLE_CLASSES.CELL_VERTICAL_HORIZONTAL_ALIGN_CENTER,
                  '!flex',
                ],
                cellRenderer: AgGroupCheckboxComponent,
                cellRendererParams: <ICheckboxCellParams>{
                  checkboxTooltip: () => {
                    return '';
                  },
                  isEditable: (node, field) => {
                    if (isSysAdmin) {
                      return true;
                    }

                    if (!loggedInUserHasAdministration) {
                      return false;
                    }

                    if (
                      node?.data?.permission_type ===
                      PermissionType.PERMISSION_UPDATE_USER_PERMISSIONS
                    ) {
                      return false;
                    }

                    return field !== userSub;
                  },
                  onChange: (node: IRowNode<UserPermissionsGridData>, field, value) => {
                    if (node.data) {
                      const permission = node.data.id;
                      const index = this.changedValues.findIndex(
                        (v) => v.sub === field && v.permission === permission
                      );
                      const obj = {
                        permission,
                        sub: field,
                        value,
                      };

                      if (index !== -1) {
                        this.changedValues.splice(index, 1);
                      } else {
                        this.changedValues.push(obj);
                      }
                    }
                  },
                },
                field: user.sub,
                headerClass: [
                  TableConstants.STYLE_CLASSES.HEADER_ALIGN_CENTER,
                  'ag-header-truncate',
                ],
                headerName: `${user.given_name} ${user.family_name?.[0]?.toUpperCase()}.`,
                headerTooltip: `${user.given_name} ${user.family_name?.[0]?.toUpperCase()}.`,
                minWidth: 100,
              };
            }),
            headerClass: TableConstants.STYLE_CLASSES.HEADER_ALIGN_CENTER,
            headerName: 'User',
          });
        }

        this.gridOptions$.next(<GridOptions<UserPermissionsGridData>>{
          ...this.gridOptions,
          columnDefs,
        });

        // await for ag-grid handling new columns
        setTimeout(() => {
          this.loading.set(false);
        }, 1000);
      });
  }

  gridReady(event: GridReadyEvent) {
    this.gridAPI.set(event.api);

    this.gridAPI()?.sizeColumnsToFit();
  }

  onDiscardChanges() {
    this.reset$.next(null);
  }

  async onExport() {
    const trialShortName = this.mainQuery.getSelectedTrial()?.short_name || '';
    const trialId = this.mainQuery.getSelectedTrial()?.id || '';
    const fileTimestamp = dayjs().format('YYYY.MM.DD-HHmmss');
    const currentTz = dayjs.tz.guess();

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: trialId,
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_entity_id: trialId || '',
          export_type: ExportType.PERMISSIONS,
          filename: `auxilius_${trialShortName}_User_Permissions_${fileTimestamp}`,
          json_properties: {
            fileTimestamp: `${fileTimestamp} ${currentTz}`,
          },
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }
  }

  onSaveChanges = async () => {
    const ref = this.overlayService.loading();

    await this.trialUserService.batchProcessPermissionUpdates(this.changedValues);
    ref.close();
  };
}
